"use client";

import type { ProductDetails, Variant } from "@raumagarn/formatters";
import { Suspense, useState } from "react";
import { AddToCartButton } from "~/components/ui/AddToCartButton";
import { AddToWishlistButton } from "~/components/ui/AddToWishlistButton";
import { MediaGallery } from "~/components/ui/MediaGallery";
import { ToolVariantPicker } from "~/components/ui/ToolSizePicker";
import { Description } from "~/features/products/ProductDetails/Description/Description";
import { TagsContainer } from "~/features/products/ProductDetails/TagsContainer";
import styles from "./ToolPDPContainer.module.css";

type TClientPDPContainer = {
	below: React.ReactNode;
	product: ProductDetails;
};

const UnsuspendedToolPDPContainer = ({ below, product }: TClientPDPContainer) => {
	const [selectedVariant, setSelectedVariant] = useState(() => {
		const firstWithStock = product?.variants?.find((variant) => {
			if (typeof variant.size?.stock === "boolean") {
				return variant.size.stock === true;
			}

			if (typeof variant.size?.stock === "string") {
				return variant.size.stock === "yes";
			}

			if (typeof variant.size?.stock === "number") {
				return variant.size?.stock > 0;
			}

			return false;
		});

		if (product?.variants?.length === 1) return firstWithStock;
		return undefined;
	});

	return (
		<div data-layout="section" className="pdp-container">
			{/* desktop gallery */}
			<div className="pdp-gallery">
				<MediaGallery media={product?.media} />
			</div>
			{/* product details */}
			<div data-layout="section" className="pdp-details">
				<div className={styles["pdp-details"]}>
					<TagsContainer>
						<div className={styles.pdpActions}>
							<AddToWishlistButton productId={product.displayItemId ?? ""} />
						</div>
					</TagsContainer>
					<h1 className={styles["product-name"]}> {product?.name}</h1>
					<Description text={product?.description} />
				</div>
				{!!product.variants?.length && product.variants.length > 1 && (
					<ToolVariantPicker
						variants={product.variants as Variant[]}
						selectedVariant={selectedVariant as Variant}
						setSelectedVariant={setSelectedVariant}
					/>
				)}

				<AddToCartButton selectedSize={selectedVariant?.size} product={product} />
				{below}
			</div>
		</div>
	);
};

export const ToolPDPContainer: typeof UnsuspendedToolPDPContainer = (props) => {
	return (
		<Suspense>
			<UnsuspendedToolPDPContainer {...props} />
		</Suspense>
	);
};

export type Size = {
	name: string;
	id: string;
	stock: boolean;
};
